import { ELEMENTS, GROUPS, NLBE_SFB_VERSION, SUBS } from "./nlsfb/data.js";

/**
 * Elementcodering volgens NLBE-SfB tabel 1 (functionele gebouwelementen), zie docs/NLSFB.md.
 * Niveaus: hoofdgroep ("5-"), element ("56", 2 cijfers), subelement ("56.2") en detail ("56.21").
 * De diepte is vrij: een asset of bevinding mag op elk niveau vanaf het element stoppen.
 */

/** Versie van de tabel; wordt bij elke code opgeslagen (`element_scheme`). */
export const NLSFB_SCHEME = NLBE_SFB_VERSION;
/** Waarde van de organisatie-instelling `element_coding` die de volledige tabel aanzet. */
export const ELEMENT_CODING = "nlbe-sfb-2025";

export type NlsfbLevel = 2 | 3 | 4;
export interface NlsfbNode {
  /** "56", "56.2" of "56.21" */
  code: string;
  label: string;
  level: NlsfbLevel;
}
/** Element (2 cijfers) met pictogram en eigen kleur. */
export interface NlsfbGroup {
  code: string;
  label: string;
  /** Naam van een lijn-icoon (lucide) in de app. */
  icon: string;
  /** Kleur van het assetsymbool (witte lijn op deze kleur; contrast ≥ 4,5:1), docs/SYMBOLEN.md. */
  color: string;
}
export interface NlsfbMain {
  /** "5" */
  code: string;
  label: string;
  /** Kleur van de hoofdgroep (filters en overzichten); het symbool zelf heeft de kleur van het element. */
  color: string;
  groups: NlsfbGroup[];
}

/** Grijs voor "niet ingedeeld" en "hoger niet genoemd". */
export const NLSFB_UNKNOWN_COLOR = "#5B6670";

const COLORS: Record<string, string> = {
  "0": "#4B5563",
  "1": "#7A5230",
  "2": "#5B6670",
  "3": "#2F6690",
  "4": "#8A6A00",
  "5": "#C2410C",
  "6": "#1D4ED8",
  "7": "#0F766E",
  "8": "#6D28D9",
  "9": "#15803D",
};

/**
 * Kleur per element (besluit 03-11-2026, docs/SYMBOLEN.md): gelijke helderheid (OKLCH L ≈ 0,5) met de
 * tint in stappen van de gulden hoek, zodat buren (56, 57) duidelijk verschillen; wit erop haalt AA.
 * "Hoger niet genoemd" (x9) is neutraal grijs.
 */
const ELEMENT_COLORS: Record<string, string> = {
  "01": "#A74449",
  "11": "#0A7D4D",
  "13": "#6F57AB",
  "16": "#8F5D03",
  "17": "#007785",
  "21": "#9D4577",
  "22": "#587502",
  "23": "#3F66B3",
  "24": "#A54A28",
  "27": "#057A68",
  "28": "#854F9C",
  "31": "#7E6604",
  "32": "#0E7397",
  "33": "#A5435B",
  "34": "#2E7C35",
  "37": "#5F5CB1",
  "41": "#9A5505",
  "42": "#10787A",
  "43": "#954886",
  "44": "#6A6F02",
  "45": "#1F6CB0",
  "47": "#A7463D",
  "52": "#0F7C5A",
  "53": "#7853A6",
  "54": "#886108",
  "55": "#0E758B",
  "56": "#A0446C",
  "57": "#4A781C",
  "58": "#4D62B3",
  "61": "#A34D17",
  "62": "#077970",
  "63": "#8B4C94",
  "64": "#776A0A",
  "65": "#0071A3",
  "66": "#A64450",
  "67": "#0E7D42",
  "71": "#6A59AD",
  "72": "#945A00",
  "73": "#117780",
  "74": "#9A467D",
  "75": "#607300",
  "76": "#3568B2",
  "77": "#A64830",
  "78": "#007B63",
  "81": "#8050A0",
  "82": "#826404",
  "83": "#0F7492",
  "84": "#A34362",
  "85": "#3A7A2D",
  "86": "#595EB2",
  "87": "#9E5208",
  "88": "#127876",
  "91": "#92498C",
  "92": "#706D06",
  "93": "#066EAE",
  "94": "#A74544",
  "95": "#077D52",
  "96": "#7355A9",
  "97": "#8D5E00",
  "98": "#057688",
};

/** Pictogram per element; "hoger niet genoemd" (x9) krijgt het neutrale doosje. */
const ICONS: Record<string, string> = {
  "01": "Building",
  "11": "Shovel",
  "13": "Rows3",
  "16": "BrickWall",
  "17": "Columns3",
  "21": "Building2",
  "22": "PanelTop",
  "23": "Layers",
  "24": "Footprints",
  "27": "Home",
  "28": "Construction",
  "31": "AppWindow",
  "32": "DoorOpen",
  "33": "SquareDashed",
  "34": "Fence",
  "37": "Sun",
  "41": "PaintRoller",
  "42": "Paintbrush",
  "43": "Grid3x3",
  "44": "Ruler",
  "45": "Grid2x2",
  "47": "Umbrella",
  "52": "Waves",
  "53": "Droplets",
  "54": "Gauge",
  "55": "FireExtinguisher",
  "56": "Heater",
  "57": "Fan",
  "58": "Thermometer",
  "61": "BatteryCharging",
  "62": "Zap",
  "63": "Lightbulb",
  "64": "Wifi",
  "65": "Siren",
  "66": "ArrowUpDown",
  "67": "Cpu",
  "71": "Signpost",
  "72": "Armchair",
  "73": "ChefHat",
  "74": "Toilet",
  "75": "Wrench",
  "76": "Archive",
  "77": "Cog",
  "78": "Palette",
  "81": "TrafficCone",
  "82": "Sofa",
  "83": "Microwave",
  "84": "Bath",
  "85": "SprayCan",
  "86": "Package",
  "87": "Projector",
  "88": "Frame",
  "91": "Mountain",
  "92": "Warehouse",
  "93": "Fence",
  "94": "TreePine",
  "95": "Pipette",
  "96": "PlugZap",
  "97": "CircleParking",
  "98": "Bike",
};

/** Hoofdgroepen 1–9 en 0, elk met hun elementen. */
export const NLSFB: readonly NlsfbMain[] = [...GROUPS]
  .map(([code, label]) => {
    const main = code.replace("-", "");
    return {
      code: main,
      label,
      color: COLORS[main] ?? "#4B5563",
      groups: ELEMENTS.filter(([c]) => c.startsWith(main)).map(([c, l]) => ({
        code: c,
        label: l,
        icon: ICONS[c] ?? "Box",
        color: ELEMENT_COLORS[c] ?? NLSFB_UNKNOWN_COLOR,
      })),
    };
  })
  .sort((a, b) => (a.code === "0" ? 1 : b.code === "0" ? -1 : a.code.localeCompare(b.code)));

/** Zonder element ("Niet ingedeeld"). */
export const NLSFB_UNKNOWN: NlsfbGroup = { code: "", label: "Niet ingedeeld", icon: "Box", color: "#5B6670" };

const groupByCode = new Map(NLSFB.flatMap((m) => m.groups.map((g) => [g.code, { group: g, main: m }] as const)));
const nodes = new Map<string, NlsfbNode>([
  ...ELEMENTS.map(([code, label]) => [code, { code, label, level: 2 as const }] as const),
  ...SUBS.map(([code, label]) => [code, { code, label, level: (code.length === 4 ? 3 : 4) as NlsfbLevel }] as const),
]);
/** Kinderen per code; een detailcode zonder subelement in de bron hangt direct onder het element. */
const children = new Map<string, NlsfbNode[]>();
for (const n of nodes.values()) {
  if (n.level === 2) continue;
  const parent = n.level === 4 && nodes.has(n.code.slice(0, 4)) ? n.code.slice(0, 4) : n.code.slice(0, 2);
  const list = children.get(parent) ?? [];
  list.push(n);
  children.set(parent, list);
}

/** Alle geldige codes (element, subelement en detail). */
export const NLSFB_CODES: readonly string[] = [...nodes.keys()];
export const isNlsfbCode = (code: string) => nodes.has(code);
export const nlsfbNode = (code: string | null | undefined) => (code ? nodes.get(code) : undefined);
/** Onderliggende codes: "56" → 56.0 … 56.9; "56.2" → 56.20 … 56.27. */
export const nlsfbChildren = (code: string): readonly NlsfbNode[] => children.get(code) ?? [];
/** Element van een code: "56.21" → "56". */
export const nlsfbGroupCode = (code: string) => code.slice(0, 2);
/** Hoofdgroep van een code: "56.21" → "5". */
export const nlsfbMainCode = (code: string) => code.slice(0, 1);

/** Pad van element naar de code zelf: "56.21" → [56, 56.2, 56.21]. */
export function nlsfbPath(code: string | null | undefined): NlsfbNode[] {
  const n = nlsfbNode(code);
  if (!n) return [];
  const path = [nodes.get(n.code.slice(0, 2))!];
  if (n.level === 4 && nodes.has(n.code.slice(0, 4))) path.push(nodes.get(n.code.slice(0, 4))!);
  if (n.level > 2) path.push(n);
  return path;
}

/** Element (met pictogram), hoofdgroep, kleur en de gekozen code zelf. */
export function nlsfb(code: string | null | undefined): {
  group: NlsfbGroup;
  main: NlsfbMain | null;
  color: string;
  node: NlsfbNode | null;
} {
  const node = nlsfbNode(code) ?? null;
  const hit = node ? groupByCode.get(nlsfbGroupCode(node.code)) : undefined;
  return hit
    ? { group: hit.group, main: hit.main, color: hit.group.color, node }
    : { group: NLSFB_UNKNOWN, main: null, color: NLSFB_UNKNOWN_COLOR, node: null };
}

/** Scheiding tussen de niveaus in een pad. */
export const NLSFB_SEP = " › ";

const letters = (s: string) => s.toLowerCase().replace(/[^\p{L}\p{N}]/gu, "");
/** Hooguit één letter verschil ("ballustrades" / "balustrades"). */
function nearlySame(a: string, b: string): boolean {
  if (a === b) return true;
  if (Math.abs(a.length - b.length) > 1) return false;
  let i = 0;
  while (i < a.length && a[i] === b[i]) i++;
  return a.slice(i + 1) === b.slice(i + 1) || a.slice(i + 1) === b.slice(i) || a.slice(i) === b.slice(i + 1);
}
/**
 * In de bron herhaalt een detail de naam van zijn subelement ("centraal, hardware"), soms met een
 * tikfout of zonder slothaakje ("opslag (buffer, koeling"). Dat deel valt weg; past het niet, dan
 * blijft de naam heel.
 */
function withoutParent(label: string, parent: string): string {
  const want = letters(parent);
  for (const m of label.matchAll(/\s*,\s*/g)) {
    if (nearlySame(letters(label.slice(0, m.index)), want)) return label.slice(m.index + m[0].length);
  }
  return label;
}

/** Namen van element tot de code zelf, zonder herhaling: "58.21" → ["Meet- en regelinstallaties", "centraal", "hardware"]. */
export function nlsfbTrail(code: string | null | undefined): string[] {
  const path = nlsfbPath(code);
  return path.map((n, i) => (i ? withoutParent(n.label, path[i - 1]!.label) : n.label));
}

/**
 * Code met alle tussenliggende niveaus vanaf het element: "58.2 Meet- en regelinstallaties › centraal",
 * "56 Verwarming en koeling" of "Niet ingedeeld". De hoofdgroep staat er niet in; zie `nlsfbFullLabel`.
 */
export const nlsfbLabel = (code: string | null | undefined) => {
  const n = nlsfbNode(code);
  return n ? `${n.code} ${nlsfbTrail(n.code).join(NLSFB_SEP)}` : NLSFB_UNKNOWN.label;
};

/** Ook met de hoofdgroep: "58.2 Installaties werktuigbouwkundig › Meet- en regelinstallaties › centraal". */
export const nlsfbFullLabel = (code: string | null | undefined) => {
  const { node, main } = nlsfb(code);
  return node && main ? `${node.code} ${[main.label, ...nlsfbTrail(node.code)].join(NLSFB_SEP)}` : NLSFB_UNKNOWN.label;
};

/** Zonder de volledige tabel (instelling uit) blijft alleen het element over. */
export const nlsfbClamp = (code: string | null, full: boolean) => (code && !full ? nlsfbGroupCode(code) : code);

/**
 * Trefwoorden uit de praktijk die niet letterlijk in de tabel staan → codes.
 * Gebruikt door het zoeken en door de voorstellen in het formulier.
 */
export const NLSFB_KEYWORDS: Readonly<Record<string, readonly string[]>> = {
  ketel: ["56.24"],
  cv: ["56.24", "56.41"],
  "cv-ketel": ["56.24"],
  radiator: ["56.61"],
  vloerverwarming: ["56.63"],
  warmtepomp: ["56.21"],
  airco: ["56.15", "56.14"],
  riool: ["52.2", "52.21"],
  afvoer: ["52"],
  verstopping: ["52.2"],
  dakgoot: ["52.12"],
  hemelwater: ["52.1"],
  regenpijp: ["52.12"],
  kraan: ["74", "53"],
  boiler: ["53"],
  waterleiding: ["53"],
  gas: ["54"],
  blusser: ["55"],
  brandslang: ["55"],
  sprinkler: ["55"],
  ventilatie: ["57"],
  wtw: ["57"],
  mv: ["57"],
  thermostaat: ["58"],
  meterkast: ["61"],
  groepenkast: ["61"],
  stopcontact: ["62"],
  lamp: ["63"],
  noodverlichting: ["63"],
  internet: ["64"],
  intercom: ["64"],
  brandmelding: ["65"],
  rookmelder: ["65"],
  alarm: ["65"],
  camera: ["65"],
  toegang: ["65"],
  lift: ["66"],
  dak: ["27", "47"],
  lekkage: ["47", "52", "53"],
  gevel: ["21", "41"],
  kozijn: ["31"],
  raam: ["31"],
  deur: ["31", "32"],
  slot: ["31", "32"],
  trap: ["24", "44"],
  hekwerk: ["34", "93"],
  plafond: ["45"],
  vloer: ["23", "43"],
  tegels: ["43", "42"],
  schilderwerk: ["41", "42"],
  toilet: ["74"],
  wc: ["74"],
  douche: ["74"],
  wastafel: ["74"],
  keuken: ["73"],
  bestrating: ["94"],
  groen: ["94"],
  parkeerplaats: ["97"],
};

const norm = (s: string) => s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

/**
 * Zoeken op code en woorden. "56.2" geeft 56.2 met zijn detailcodes; "ketel" geeft 56.2x.
 * Zonder volledige tabel (`full: false`) alleen elementen.
 */
export function nlsfbSearch(q: string, opts: { full?: boolean; limit?: number } = {}): NlsfbNode[] {
  const full = opts.full ?? true;
  const limit = opts.limit ?? 40;
  const t = norm(q.trim());
  if (!t) return [];
  const pool = [...nodes.values()].filter((n) => full || n.level === 2);
  if (/^\d/.test(t)) {
    return pool.filter((n) => n.code.startsWith(t)).slice(0, limit);
  }
  const words = t.split(/[\s,]+/).filter((w) => w.length >= 2);
  const score = new Map<string, number>();
  for (const n of pool) {
    const g = groupByCode.get(nlsfbGroupCode(n.code));
    const text = norm(`${n.label} ${g?.group.label ?? ""}`);
    let s = 0;
    for (const w of words) if (text.includes(w)) s += n.level === 2 ? 1.1 : 1;
    if (s) score.set(n.code, s);
  }
  for (const w of words) {
    for (const c of NLSFB_KEYWORDS[w] ?? []) {
      const code = full ? c : nlsfbGroupCode(c);
      if (nodes.has(code)) score.set(code, (score.get(code) ?? 0) + 2);
    }
  }
  // Meerdere woorden die hetzelfde element raken ("lekkage riool" → 52) wegen zwaarder.
  const wordsPerElement = new Map<string, Set<string>>();
  for (const w of words) {
    const hits = [...(NLSFB_KEYWORDS[w] ?? []), ...pool.filter((n) => norm(n.label).includes(w)).map((n) => n.code)];
    for (const c of hits) {
      const set = wordsPerElement.get(nlsfbGroupCode(c)) ?? new Set();
      set.add(w);
      wordsPerElement.set(nlsfbGroupCode(c), set);
    }
  }
  for (const [code, s] of score) {
    const n = wordsPerElement.get(nlsfbGroupCode(code))?.size ?? 0;
    if (n > 1) score.set(code, s + 1.5 * (n - 1));
  }
  return [...score.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, limit)
    .map(([c]) => nodes.get(c)!);
}

/** Element per type bevinding (eerste suggestie als er niets beters is). */
const TYPE_ELEMENTS: Record<string, readonly string[]> = {
  bouwkundig: ["21", "27"],
  w: ["56", "57"],
  e: ["62", "63"],
  sanitair: ["52", "53", "74"],
  brand: ["65", "55"],
  toegang: ["65", "31"],
  afwerking: ["42", "43", "45"],
  buiten: ["94", "93"],
};

/**
 * Een tot drie voorstellen voor een bevinding: eerst de code van de asset, dan trefwoorden uit
 * titel en toelichting, dan het type. Geen externe AI.
 */
export function suggestElements(
  input: { type?: string | null; text?: string | null; assetCode?: string | null },
  opts: { full?: boolean } = {},
): string[] {
  const full = opts.full ?? true;
  const out: string[] = [];
  const add = (c: string | null | undefined) => {
    const code = c ? nlsfbClamp(c, full) : null;
    if (code && nodes.has(code) && !out.includes(code)) out.push(code);
  };
  add(input.assetCode);
  if (input.text) for (const n of nlsfbSearch(input.text, { full, limit: 3 })) add(n.code);
  for (const c of TYPE_ELEMENTS[input.type ?? ""] ?? []) add(c);
  return out.slice(0, 3);
}

/** Oude installatiecategorieën → element (import uit het prototype). */
export const LEGACY_CATEGORY_TO_NLSFB: Record<string, string | null> = {
  Verwarming: "56",
  Koeling: "56",
  Ventilatie: "57",
  Elektra: "62",
  Sanitair: "74",
  Brandmelding: "65",
  Liften: "66",
  Toegang: "65",
  Overig: null,
};
